import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';

import { IconComponent } from '@shared/components/icon/icon.component';
import {
  ConfirmationActionModalComponent,
  ConfirmationActionModalData,
} from '@shared/components/modals/confirmation-action-modal/confirmation-action-modal.components';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { GqlService, listUserNamesWithEmailQuery } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { QuarterCloseChecklistPeriodCloseService } from '@shared/services/quarter-close-checklist-period-close.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { MainQuery } from '@shared/store/main/main.query';
import { QuarterCloseNotesQuery } from '@shared/store/quarter-close-notes/quarter-close-notes.query';
import {
  MonthlyReviewNote,
  QuarterCloseNotesStore,
} from '@shared/store/quarter-close-notes/quarter-close-notes.store';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, TooltipDirective, DatePipe],
  selector: 'aux-monthly-review-record',
  templateUrl: './monthly-review-record.component.html',
})
export class MonthlyReviewRecordComponent {
  private readonly authQuery = inject(AuthQuery);
  private readonly destroyRef = inject(DestroyRef);
  private readonly gqlService = inject(GqlService);
  private readonly mainQuery = inject(MainQuery);
  private readonly overlayService = inject(OverlayService);
  private readonly periodCloseService = inject(QuarterCloseChecklistPeriodCloseService);
  private readonly quarterCloseNotesQuery = inject(QuarterCloseNotesQuery);
  private readonly quarterCloseNotesStore = inject(QuarterCloseNotesStore);

  pending = false;
  @Input() record!: MonthlyReviewNote;

  get author(): string {
    const author = this.mainQuery
      .userMap()
      .get(this.record.createdBy) as listUserNamesWithEmailQuery;

    return author.email.includes('@auxili.us') && !this.authQuery.isAuxAdmin()
      ? 'Auxilius Expert'
      : `${author.given_name} ${author.family_name.slice(0, 1)}.`;
  }

  async onRemove(): Promise<void> {
    this.pending = true;

    const ref = this.overlayService.openPopup<ConfirmationActionModalData, boolean>({
      data: {
        executeActionText: 'Do you want to proceed?',
        message: 'This will permanently delete the Monthly Review Note.',
        skipKeywordConfirmation: true,
        width: '370px',
      },
      modal: ConfirmationActionModalComponent,
      settings: {
        header: 'Delete Monthly Review Note?',
        primaryButton: {
          label: 'Delete',
          variant: 'negative',
        },
      },
    });

    const { data: refData } = await firstValueFrom(ref.afterClosed$);

    if (refData) {
      this.gqlService
        .removeNote$(this.record.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(({ data, errors }) => {
          if (errors.length) {
            this.overlayService.error(errors);
            this.pending = false;
          } else if (data) {
            this.quarterCloseNotesStore.removeMonthlyReviewNote(
              this.record.workflowId as string,
              this.record.id
            );
          }
        });
    } else {
      this.pending = false;
    }
  }

  get removeTooltip(): string {
    const author = this.mainQuery
      .userMap()
      .get(this.record.createdBy) as listUserNamesWithEmailQuery;

    if (author.sub !== this.authQuery.user().sub) {
      return 'You can only delete your own notes.';
    }

    if (this.periodCloseService.currentOpenMonth !== this.quarterCloseNotesQuery.selectedPeriod()) {
      return 'Monthly Review Notes cannot be deleted from closed months.';
    }

    return '';
  }
}
